import { defineStore } from 'pinia'
import type {
  ChatMessage,
  ChatResponse,
  ConversationMetadata,
  ConfidenceLevel,
  ChatContext,
  ChatRequest,
} from '@/types/chat'
import { ChatType, ChatResponseType } from '@/types/chatEnums'
import { useRuntimeConfig } from '#imports'
import { v4 as uuidv4 } from 'uuid'
import { useLogger } from '@/composables/useLogger'
import { toUTCString } from '@/utils/dates'
// Import helpers from the new utility file
import { parseTimestamp, generateChatId, getUserLocation } from '@/utils/chatUtils'

const { log, warn, logError } = useLogger()

// --- Add ConversationApiResponse interface --- (Moved from globalChat.ts)
interface ConversationApiResponse {
  id: string
  title: string | null
  summary: string | null
  relevantBubbles?: string[]
  lastMessage?: string
  bubbleCount: number
  createdAt: string // API sends date as string
  updatedAt: string // API sends date as string
  lastConfidence?: ConfidenceLevel
}

// --- State Interfaces (extracted from globalChat for clarity) ---

interface Conversation {
  id: string
  title: string | null
  summary: string | null
  relevantBubbles: string[]
  lastMessage?: string // Keep track of the last user message text
  bubbleCount: number // Potentially from API, or calculated
  createdAt: Date
  updatedAt: Date
  lastConfidence?: ConfidenceLevel
  // Messages are now stored in the central `messages` map
}

interface CurrentConversationState {
  id: string
  title: string | null
  summary: string | null
  relevantBubbles: string[]
  confidence?: ConfidenceLevel
}

interface ChatState {
  // Core State (from chatCommon)
  messages: Map<string, ChatMessage[]> // Key: chatId (`type-identifier`)
  loading: boolean // General loading state (e.g., for history, conversations)
  streamStatus: Map<string, boolean> // Key: chatId, tracks active SSE streams - Still useful for low-level stream tracking?
  currentResponse: Map<string, string> // Key: chatId, holds incoming assistant message chunks

  // Active Chat Context
  activeChatId: string | null // Combines type and identifier, e.g., "bubble-uuid" or "global-uuid"
  activeChatType: ChatType | null
  activeIdentifier: string | null

  // Global Chat Specific State (from globalChat)
  conversations: Conversation[] // List of global conversations
  currentConversationDetails: CurrentConversationState | null // Holds details only for the *active* global conversation

  // UI Feedback State
  isTyping: boolean // User typing/thinking indicator
  isStreaming: boolean // Flag for when chunks are actively being received
  isLoadingBubbles: boolean // Specific loading indicator for global chat relevant bubbles
  error: string | null // General error state
}

// --- Initial State ---

const initialState: ChatState = {
  messages: new Map(),
  loading: false,
  streamStatus: new Map(),
  currentResponse: new Map(),
  activeChatId: null,
  activeChatType: null,
  activeIdentifier: null,
  conversations: [],
  currentConversationDetails: null,
  isTyping: false,
  isStreaming: false,
  isLoadingBubbles: false,
  error: null,
}

// --- Store Definition ---

export const useChatStore = defineStore('chat', {
  state: (): ChatState => ({ ...initialState }),

  getters: {
    // --- Computed State ---

    /** Gets messages for the currently active chat */
    activeMessages: (state): ChatMessage[] => {
      if (!state.activeChatId) return []
      return state.messages.get(state.activeChatId) || []
    },

    /** Gets the current incomplete response for the active chat */
    activeCurrentResponse: (state): string => {
      if (!state.activeChatId) return ''
      return state.currentResponse.get(state.activeChatId) || ''
    },

    // --- Global Chat Specific Getters ---

    /** Sorted list of global conversations by update time */
    sortedConversations: (state): Conversation[] => {
      // Ensure consistent date objects before sorting
      return [...state.conversations]
        .map((conv) => ({ ...conv, updatedAt: parseTimestamp(conv.updatedAt) })) // Parse just in case
        .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
    },

    /** Checks if there's an unused global conversation */
    hasUnusedGlobalConversation: (state): boolean => {
      // If the current active chat is global and has no messages, it might be the "unused" one.
      const isActiveGlobalAndEmpty =
        state.activeChatType === ChatType.GLOBAL &&
        state.activeChatId &&
        (state.messages.get(state.activeChatId)?.length ?? 0) === 0

      // Find a conversation in the list that is NOT the active one (if the active one is empty global)
      // OR find any conversation if the active one is not an empty global one.
      // The conversation must also have no title/last message and no messages stored for its ID.
      const unusedConv = state.conversations.find((conv) => {
        const chatId = generateChatId(ChatType.GLOBAL, conv.id)
        const isTheActiveEmptyGlobal = isActiveGlobalAndEmpty && state.activeChatId === chatId

        return (
          !isTheActiveEmptyGlobal && // Exclude the currently active empty global conv from being "unused" in this context
          !conv.lastMessage &&
          conv.title === null &&
          (state.messages.get(chatId)?.length ?? 0) === 0
        )
      })
      log('🎯 Checking hasUnusedGlobalConversation:', {
        currentActiveId: state.activeChatId,
        isActiveGlobalAndEmpty,
        foundUnusedId: unusedConv?.id,
      })
      return !!unusedConv
    },
  },

  actions: {
    // --- Basic State Management Actions ---

    /** Sets the currently active chat context. */
    setActiveChat(type: ChatType, identifier: string) {
      log(`🔧 setActiveChat called with type: "${String(type)}", identifier: "${identifier}"`)
      if (!type) {
        logError(
          'setActiveChat: type argument is null or undefined before calling generateChatId!',
          { type, identifier }
        )
      }
      const newChatId = generateChatId(type, identifier)
      log(`🎯 Setting active chat: ${newChatId}`)
      this.activeChatType = type
      this.activeIdentifier = identifier
      this.activeChatId = newChatId

      // Reset chat-specific states if needed (e.g., when switching)
      this.isTyping = false // Reset typing indicator on chat switch
      this.isLoadingBubbles = false // Reset global-specific loaders

      // If switching to a global chat, ensure its details are loaded/set
      if (type === ChatType.GLOBAL) {
        const conversation = this.conversations.find((c) => c.id === identifier)
        if (conversation) {
          // Ensure currentConversationDetails reflects the active global chat
          this.currentConversationDetails = {
            id: conversation.id,
            title: conversation.title,
            summary: conversation.summary,
            relevantBubbles: conversation.relevantBubbles || [],
            confidence: conversation.lastConfidence,
          }
        } else {
          // This might happen if setCurrentConversation hasn't populated the list yet
          // Or if it's a brand new conversation not yet in the main list
          logError(
            `setActiveChat called for global chat ${identifier} but not found in conversations list.`
          )
          this.currentConversationDetails = null // Clear details if conversation not found
        }
      } else {
        // Clear global details if switching away from a global chat
        this.currentConversationDetails = null
      }

      // Ensure messages map has an entry for the new chat ID
      if (!this.messages.has(newChatId)) {
        this.messages.set(newChatId, [])
      }

      // Note: History loading might be triggered separately (e.g., in setCurrentConversation for global, or on component mount)
    },

    /** Sets the main loading state. */
    setLoading(status: boolean) {
      this.loading = status
    },

    /** Sets the error state. */
    setError(message: string | null) {
      this.error = message
      if (message) {
        logError('Chat Store Error Set:', message)
      }
    },

    /** Sets the typing indicator state. */
    setTyping(status: boolean) {
      this.isTyping = status
    },

    // --- Core Streaming & Message Handling ---

    /** Updates the content of the last assistant message or adds a new one. */
    _updateAssistantMessage(chatId: string, timestamp?: string | Date) {
      // Ensure messages for this chat ID exist
      if (!this.messages.has(chatId)) {
        warn(
          `_updateAssistantMessage called for chatId "${chatId}" but no messages map entry exists. Initializing.`
        )
        this.messages.set(chatId, [])
      }

      const currentMessages = this.messages.get(chatId)! // We know it exists now
      const messages = [...currentMessages] // Work with a copy
      const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null
      const currentContent = this.currentResponse.get(chatId) || ''

      const messageTimestamp = parseTimestamp(timestamp || toUTCString()) // Ensure Date object

      if (lastMessage?.role === 'assistant') {
        lastMessage.content = currentContent
        lastMessage.timestamp = messageTimestamp // Always update timestamp during streaming
        log(`🎯 Updated assistant message chunk for ${chatId}`)
      } else {
        const newMessage: ChatMessage = {
          role: 'assistant',
          content: currentContent,
          timestamp: messageTimestamp,
        }
        messages.push(newMessage)
        log(`🎯 Added new assistant message chunk for ${chatId}`)
      }

      // Sort messages by timestamp (use getTime() for reliable comparison)
      const sortedMessages = messages.sort((a, b) => {
        const timeA = (
          a.timestamp instanceof Date ? a.timestamp : parseTimestamp(a.timestamp)
        ).getTime()
        const timeB = (
          b.timestamp instanceof Date ? b.timestamp : parseTimestamp(b.timestamp)
        ).getTime()
        return timeA - timeB
      })

      // Update the messages map
      this.messages.set(chatId, sortedMessages)

      // Also update last message timestamp in global conversation list if applicable
      if (this.activeChatType === ChatType.GLOBAL && this.activeIdentifier) {
        const conversation = this.conversations.find((c) => c.id === this.activeIdentifier)
        if (conversation) {
          conversation.updatedAt = messageTimestamp // Keep conversation updatedAt fresh
        }
      }
    },

    /** Handles basic stream responses (start, chunk, end) common to all chat types. */
    _handleBasicStreamResponse(chatId: string, response: ChatResponse) {
      log(`⚡ Handling basic stream response for ${chatId}:`, response.type)
      switch (response.type) {
        case ChatResponseType.START:
          this.currentResponse.set(chatId, '')
          // Don't set streaming/typing here, wait for first chunk
          this.isLoadingBubbles = this.activeChatType === ChatType.GLOBAL // Start loading bubbles for global
          break

        case ChatResponseType.CHUNK: {
          // --- First Chunk Logic ---
          if (!this.isStreaming) {
            // Check if this is the first chunk
            this.isStreaming = true // Mark as streaming now
            this.isTyping = false // Stop "thinking" indicator
            log(`🔌 First chunk received for ${chatId}, starting stream display.`)
          }
          // --- End First Chunk Logic ---

          if (response.content === undefined || response.content === null) {
            warn(`Received chunk for ${chatId} with null/undefined content. Skipping.`)
            break
          }
          const currentContent = this.currentResponse.get(chatId) || ''
          this.currentResponse.set(chatId, currentContent + response.content)
          this._updateAssistantMessage(chatId, response.timestamp)
          break
        }

        case ChatResponseType.END:
          // Ensure both flags are false on end
          this.isStreaming = false
          this.isTyping = false
          this.isLoadingBubbles = false // Stop bubble loading on stream end
          this._updateAssistantMessage(chatId, response.timestamp)
          this.currentResponse.delete(chatId)
          break
      }
    },

    /** Handles stream response types specific to Global Chat. */
    _handleGlobalStreamResponse(chatId: string, response: ChatResponse) {
      log(`🌍 Handling global stream response for ${chatId}:`, response.type)
      if (
        !this.currentConversationDetails ||
        this.currentConversationDetails.id !== this.activeIdentifier
      ) {
        logError(
          `Received global stream response for ${chatId}, but currentConversationDetails is not set or mismatched.`
        )
        return
      }

      const conversation = this.conversations.find((c) => c.id === this.activeIdentifier)
      if (!conversation) {
        logError(
          `Received global stream response for ${chatId}, but conversation not found in list.`
        )
        return
      }

      switch (response.type) {
        case ChatResponseType.RELEVANT_BUBBLES: {
          this.isLoadingBubbles = false // Bubbles received
          const bubbles = response.content as string[] // Assuming content is string[]
          if (Array.isArray(bubbles)) {
            this.currentConversationDetails.relevantBubbles = bubbles
            conversation.relevantBubbles = bubbles // Update list item too
          } else {
            logError('Invalid relevant_bubbles content received:', response.content)
          }
          break
        }
        case ChatResponseType.COMPLETE: {
          const metadata = response.content as unknown as ConversationMetadata
          log(`✅ Received COMPLETE metadata for ${chatId}:`, metadata)
          if (!metadata || typeof metadata !== 'object' || !('title' in metadata)) {
            logError('Invalid metadata format received in COMPLETE event:', metadata)
            // Don't throw, just log and potentially ignore
            return
          }
          // Update both conversation list and current conversation details
          if (conversation) {
            conversation.title = metadata.title
            conversation.summary = metadata.summary
            conversation.relevantBubbles = metadata.relevantBubbles || conversation.relevantBubbles // Use existing if not provided
            conversation.lastConfidence = metadata.confidence
            conversation.updatedAt = parseTimestamp(metadata.updated_at || new Date()) // Ensure Date
          }
          if (this.currentConversationDetails) {
            this.currentConversationDetails.title = metadata.title
            this.currentConversationDetails.summary = metadata.summary
            this.currentConversationDetails.relevantBubbles =
              metadata.relevantBubbles || this.currentConversationDetails.relevantBubbles
            this.currentConversationDetails.confidence = metadata.confidence
          }
          // Ensure the conversation list triggers reactivity if needed elsewhere
          // this.$patch({ conversations: [...this.conversations] }); // Might not be needed if direct mutation is reactive
          break
        }
        // START, CHUNK, END are handled by _handleBasicStreamResponse
      }
    },

    /** Initiates a stream request to the backend chat endpoint. */
    async _streamRequest(
      url: string,
      payload: { message: ChatMessage; chatRequest?: ChatRequest | null },
      chatId: string
    ) {
      log(`🚀 Initiating stream request to ${url} for ${chatId}`, payload)
      // Add user message to local state immediately
      try {
        // Ensure messages for this chat ID exist
        if (!this.messages.has(chatId)) {
          warn(
            `_streamRequest initiated for chatId "${chatId}" but no messages map entry exists. Initializing.`
          )
          this.messages.set(chatId, [])
        }
        const currentMessages = this.messages.get(chatId)!
        if (!Array.isArray(currentMessages)) {
          // This shouldn't happen due to the check above, but belts and braces
          warn(`Messages for ${chatId} was not an array after init, resetting.`)
          this.messages.set(chatId, [])
        }
        const messageArray = [...this.messages.get(chatId)!] // Get a fresh copy *after* potential init
        // Add user message with a parsed timestamp
        payload.message.timestamp = parseTimestamp(payload.message.timestamp)
        this.messages.set(chatId, [...messageArray, payload.message])

        // Update conversation's last message and timestamp if global
        if (this.activeChatType === ChatType.GLOBAL && this.activeIdentifier) {
          const conversation = this.conversations.find((c) => c.id === this.activeIdentifier)
          if (conversation) {
            conversation.lastMessage = payload.message.content
            conversation.updatedAt = payload.message.timestamp
          }
        }
      } catch (error) {
        logError(`Error adding user message locally for ${chatId}:`, error)
        this.setError(
          `Failed to add user message locally: ${error instanceof Error ? error.message : String(error)}`
        )
        this.isTyping = false // Stop indicators on error
        this.isLoadingBubbles = false
        return // Don't proceed with fetch if local update fails
      }

      // Set initial states *before* fetch
      this.streamStatus.set(chatId, false) // Not streaming yet
      this.isStreaming = false // Explicitly false
      // isTyping is now set in sendMessage *before* calling this
      this.setError(null)
      this.isLoadingBubbles = this.activeChatType === ChatType.GLOBAL // Still okay to start this early

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify(payload),
        })

        log(`🔌 Stream request response status for ${chatId}: ${response.status}`)

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`Request failed: ${response.status} ${errorText}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body reader available.')
        }

        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            log(`🏁 Stream finished for ${chatId}`)
            // Ensure final state cleanup even if END event wasn't received
            if (this.isStreaming || this.isTyping) {
              this._handleBasicStreamResponse(chatId, {
                type: ChatResponseType.END,
                content: '',
                timestamp: new Date(),
              })
            }
            break
          }

          const chunk = new TextDecoder().decode(value)
          buffer += chunk

          let newlineIndex
          while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
            const line = buffer.slice(0, newlineIndex).trim()
            buffer = buffer.slice(newlineIndex + 1)

            if (!line || !line.startsWith('data: ')) continue // Skip empty lines or non-SSE lines

            try {
              const jsonStr = line.slice(6) // Remove "data: " prefix
              const streamResponse = JSON.parse(jsonStr) as ChatResponse

              // --- Centralized Response Handling ---
              // Call specific handlers first
              if (
                this.activeChatType === ChatType.GLOBAL &&
                this.activeIdentifier &&
                chatId === generateChatId(ChatType.GLOBAL, this.activeIdentifier)
              ) {
                this._handleGlobalStreamResponse(chatId, streamResponse)
              }
              // Then call basic handler (handles START, CHUNK, END state updates)
              this._handleBasicStreamResponse(chatId, streamResponse)
            } catch (error) {
              logError(`Error parsing stream response JSON for ${chatId}:`, error, 'Line:', line)
              // Decide how to handle parse errors, maybe ignore the line?
            }
          }
        } // end while(true) reader loop
      } catch (error) {
        logError(`Error during stream request or processing for ${chatId}:`, error)
        this.setError(`Stream error: ${error instanceof Error ? error.message : String(error)}`)
        // Ensure cleanup on error
        this._handleBasicStreamResponse(chatId, {
          type: ChatResponseType.END,
          content: '',
          timestamp: new Date(),
        })
      }
    },

    // --- History & Cleanup Actions ---

    /** Fetches chat history from the backend for a specific chat. */
    async getChatHistory(type: ChatType, identifier: string) {
      const chatId = generateChatId(type, identifier)
      log(`⏳ Fetching history for ${chatId}...`)
      this.setLoading(true)
      this.setError(null)

      try {
        const config = useRuntimeConfig()
        const response = await fetch(
          `${config.public.apiBase}/chat/${type.toLowerCase()}/${identifier}/history`,
          {
            credentials: 'include', // Ensure cookies are sent
          }
        )

        log(`Histor fetch status for ${chatId}: ${response.status}`)
        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`Failed to fetch chat history (${response.status}): ${errorText}`)
        }

        const history = await response.json()

        if (!Array.isArray(history)) {
          warn(`🎯 History received for ${chatId} was not an array, resetting.`, history)
          this.messages.set(chatId, [])
        } else {
          // Parse timestamps and sort messages
          const processedHistory = history
            .map((msg: ChatMessage) => ({
              ...msg,
              timestamp: parseTimestamp(msg.timestamp), // Ensure Date objects
            }))
            .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()) // Sort by time

          log(
            `✅ History received and processed for ${chatId}: ${processedHistory.length} messages`
          )
          this.messages.set(chatId, processedHistory)
        }
      } catch (error) {
        logError(`Error fetching chat history for ${chatId}:`, error)
        this.setError(
          `Failed to load history: ${error instanceof Error ? error.message : String(error)}`
        )
        // Don't clear existing messages on error, just report it
      } finally {
        this.setLoading(false)
      }
    },

    /** Clears chat history on the backend and locally for a specific chat. */
    async clearHistory(type: ChatType, identifier: string) {
      const chatId = generateChatId(type, identifier)
      log(`🗑️ Clearing history for ${chatId}...`)
      this.setLoading(true)
      this.setError(null)

      try {
        const config = useRuntimeConfig()
        const response = await fetch(
          // Ensure API endpoint matches backend route (e.g., /chat/{type}/{id}/messages or /history)
          `${config.public.apiBase}/chat/${type.toLowerCase()}/${identifier}/messages`,
          {
            method: 'DELETE',
            credentials: 'include',
          }
        )

        log(`Clear history response status for ${chatId}: ${response.status}`)
        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`Failed to clear chat history (${response.status}): ${errorText}`)
        }

        // Clear local data on successful deletion
        this.messages.delete(chatId)
        this.currentResponse.delete(chatId)
        this.streamStatus.delete(chatId)

        // If clearing the active chat, reset relevant state
        if (this.activeChatId === chatId) {
          this.activeMessages.length = 0 // Clear the getter's source
          this.isTyping = false
          this.isLoadingBubbles = false
          if (type === ChatType.GLOBAL) {
            this.currentConversationDetails = null // Clear global specific details
            // Maybe create a new blank conversation? Depends on desired UX.
            // For now, just clear local state. UI should react.
          }
        }

        // Special handling if clearing a global conversation that's in the list
        if (type === ChatType.GLOBAL) {
          const index = this.conversations.findIndex((c) => c.id === identifier)
          if (index > -1) {
            // Option 1: Reset the conversation in the list to a 'new' state
            // this.conversations[index].title = null;
            // this.conversations[index].summary = null;
            // this.conversations[index].lastMessage = undefined;
            // this.conversations[index].updatedAt = new Date(); // Reset timestamp
            // this.conversations[index].messages = []; // Not strictly needed as messages are in map

            // Option 2: Remove it entirely? This might be better if clear means delete.
            this.conversations.splice(index, 1)

            log(`✅ Cleared and removed global conversation ${identifier} from list.`)
          }
        }

        log(`✅ History cleared successfully for ${chatId}`)
        // Maybe return true? For now, success is indicated by lack of error.
      } catch (error) {
        logError(`Failed to clear chat history for ${chatId}:`, error)
        this.setError(
          `Failed to clear history: ${error instanceof Error ? error.message : String(error)}`
        )
        // Maybe return false?
      } finally {
        this.setLoading(false)
      }
    },

    /** Cleans up local state associated with a specific chat ID. */
    cleanupChat(chatId: string) {
      log(`🧹 Cleaning up local state for chat: ${chatId}`)
      this.messages.delete(chatId)
      this.streamStatus.delete(chatId)
      this.currentResponse.delete(chatId)

      // If cleaning up the currently active chat, reset active state
      if (this.activeChatId === chatId) {
        log(`🧹 Also resetting active chat context as it matched ${chatId}`)
        this.activeChatId = null
        this.activeChatType = null
        this.activeIdentifier = null
        this.currentConversationDetails = null // Clear global details too
        this.isTyping = false
        this.isLoadingBubbles = false
        this.setError(null) // Clear any errors related to the active chat
      }
    },

    /** Resets the entire chat store to its initial state. */
    resetState() {
      log('🔄 Resetting chat store state')
      // Use Pinia's built-in $reset method
      this.$reset()
      // If $reset wasn't available or customized:
      // Object.assign(this, initialState)
      log('✅ Chat store state reset complete')
    },

    // --- Main User Action ---

    /** Sends a message to the currently active chat, including context within ChatRequest. */
    async sendMessage(content: string) {
      if (!this.activeChatId || !this.activeChatType || !this.activeIdentifier) {
        logError('sendMessage called but no active chat is set.')
        this.setError('Cannot send message: No active chat selected.')
        return
      }
      if (!content?.trim()) {
        warn('sendMessage called with empty content. Ignoring.')
        return // Don't send empty messages
      }

      // Set Typing state immediately
      this.isTyping = true

      const chatId = this.activeChatId
      const type = this.activeChatType
      const identifier = this.activeIdentifier

      log(`✉️ Sending message to ${chatId}:`, content.substring(0, 50) + '...')

      // Create user message object
      const message: ChatMessage = {
        role: 'user',
        content: content.trim(),
        timestamp: parseTimestamp(toUTCString() + 'Z'),
      }

      // Gather Context
      let context: ChatContext | null = null
      try {
        log('Gathering chat context...')
        const userLocation = await getUserLocation() // Use camelCase variable
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null // Use camelCase variable
        const userCurrentTime = new Date().toISOString() // Use camelCase variable

        let focusUuids: string[] | null = null // Use camelCase variable
        if (type === ChatType.GLOBAL && this.currentConversationDetails) {
          focusUuids = this.currentConversationDetails.relevantBubbles || []
        } else if (type === ChatType.COLLECTION || type === ChatType.BUBBLE) {
          focusUuids = [identifier]
        }

        // Construct context object with camelCase keys
        context = {
          focusUuids,
          userLocation,
          userTimezone,
          userCurrentTime,
        }
        log('Context gathered:', context)
      } catch (err) {
        logError('Failed to gather chat context:', err)
      }

      // --- Construct ChatRequest ---
      const chatRequest: ChatRequest = {
        context: context,
        identifier: identifier,
        chatType: type, // Use camelCase key
      }
      // --- End Construct ChatRequest ---

      try {
        const config = useRuntimeConfig()
        const url = `${config.public.apiBase}/chat/${type.toLowerCase()}/${identifier}`

        // Construct the final payload with camelCase key 'chatRequest'
        const payload = { message, chatRequest } // Use camelCase key here

        await this._streamRequest(url, payload, chatId)

        log(`✅ sendMessage flow initiated for ${chatId}`)
      } catch (error) {
        logError(`Error initiating sendMessage for ${chatId}:`, error)
        this.setError(
          `Failed to send message: ${error instanceof Error ? error.message : String(error)}`
        )
        // Ensure UI state is reset fully on error
        this.isTyping = false // Reset typing on error
        this.isStreaming = false // Reset streaming on error
        this.isLoadingBubbles = false
        if (chatId) this.streamStatus.set(chatId, false) // Use actual chatId if available
      }
    },

    // --- Global Chat Specific Actions ---

    /** Loads the list of global conversations from the backend. */
    async loadConversations() {
      log('🌍 Loading global conversations...')
      this.setLoading(true)
      this.setError(null)
      try {
        const config = useRuntimeConfig()
        const response = await fetch(`${config.public.apiBase}/chat/conversations`, {
          credentials: 'include',
        })

        log(`Load conversations response status: ${response.status}`)
        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`Failed to load conversations (${response.status}): ${errorText}`)
        }

        const data = (await response.json()) as ConversationApiResponse[] // Type assertion for API response

        // Map API response to store's Conversation interface, parsing dates
        this.conversations = data.map((conv) => ({
          id: conv.id,
          title: conv.title,
          summary: conv.summary,
          relevantBubbles: conv.relevantBubbles || [],
          lastMessage: conv.lastMessage,
          bubbleCount: conv.bubbleCount,
          createdAt: parseTimestamp(conv.createdAt), // Use parser
          updatedAt: parseTimestamp(conv.updatedAt), // Use parser
          lastConfidence: conv.lastConfidence,
        }))

        log(`✅ Loaded ${this.conversations.length} global conversations.`)
      } catch (error) {
        logError('Failed to load conversations:', error)
        this.setError(
          `Failed to load conversations: ${error instanceof Error ? error.message : String(error)}`
        )
        this.conversations = [] // Clear conversations on error
      } finally {
        this.setLoading(false)
      }
    },

    /**
     * Selects an existing global conversation or starts a new one if necessary.
     * Ensures an active global chat context is set.
     * Returns the ID of the conversation that becomes active.
     * @param preferredId - Optionally attempt to activate this specific ID first.
     * @param forceNew - If true, bypasses keeping the current active chat and forces activation of an unused or new one.
     */
    async ensureGlobalConversationActive(
      preferredId?: string,
      forceNew: boolean = false
    ): Promise<string> {
      log('🌍 Ensuring global conversation active...', { preferredId, forceNew })

      // 1. If a specific valid ID is preferred and exists, try to set it active (Only if not forcing new)
      // This check should probably happen *after* the forceNew check? Or maybe preferredId implies not forcing new?
      // Let's refine: If forceNew is true, preferredId is ignored for activation purpose, but maybe used later?
      // Safest: Skip check 1 and 2 if forceNew is true.
      if (!forceNew) {
        // Check 1: Preferred ID
        if (preferredId) {
          const preferredExists = this.conversations.some((c) => c.id === preferredId)
          if (preferredExists) {
            log(`Trying to set preferred global conversation: ${preferredId}`)
            await this.setCurrentConversation(preferredId)
            if (this.activeChatType === ChatType.GLOBAL && this.activeIdentifier === preferredId) {
              return preferredId
            } else {
              warn(`Failed to set preferred conversation ${preferredId} as active. Continuing...`)
            }
          } else {
            warn(`Preferred conversation ID ${preferredId} not found. Ignoring preference.`)
          }
        }

        // Check 2: Keep existing active if valid
        if (
          this.activeChatType === ChatType.GLOBAL &&
          this.activeIdentifier &&
          this.conversations.some((c) => c.id === this.activeIdentifier)
        ) {
          log(
            `Already active global conversation: ${this.activeIdentifier}. Keeping it (forceNew=false).`
          )
          return this.activeIdentifier
        }
      } else {
        log('forceNew=true, skipping checks for preferred/active conversation.')
      }

      // 3. Check for an existing "empty" conversation in the list (no title/last message, no messages in map)
      const unusedConv = this.conversations.find((conv) => {
        // Ensure it's not the *current* active one if forceNew was true (we want a *different* one)
        const isCurrentActive =
          this.activeChatType === ChatType.GLOBAL && this.activeIdentifier === conv.id
        if (forceNew && isCurrentActive) return false // Skip the current one if forcing new

        const chatId = generateChatId(ChatType.GLOBAL, conv.id)
        return (
          !conv.lastMessage && conv.title === null && (this.messages.get(chatId)?.length ?? 0) === 0
        )
      })

      if (unusedConv) {
        log(`Found existing unused conversation: ${unusedConv.id}. Setting it active.`)
        await this.setCurrentConversation(unusedConv.id)
        // Ensure it actually became active before returning
        if (this.activeChatType === ChatType.GLOBAL && this.activeIdentifier === unusedConv.id) {
          return unusedConv.id
        } else {
          logError(
            `Failed to make unused conversation ${unusedConv.id} active. Attempting to create new.`
          )
          // Fall through to create new if setting unused one failed
        }
      }

      // 4. If none of the above, create a new one
      log('No suitable existing/unused conversation found or activated. Creating a new one.')
      const conversationId = uuidv4()
      const newConversation: Conversation = {
        id: conversationId,
        title: null,
        summary: null,
        relevantBubbles: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        bubbleCount: 0,
      }

      this.conversations.unshift(newConversation)
      this.messages.set(generateChatId(ChatType.GLOBAL, conversationId), [])

      await this.setCurrentConversation(conversationId)
      // Final check after attempting to set the newly created one
      if (this.activeChatType === ChatType.GLOBAL && this.activeIdentifier === conversationId) {
        return conversationId
      } else {
        logError(
          `Critical error: Failed to set newly created conversation ${conversationId} as active.`
        )
        // What to return here? Maybe throw?
        throw new Error('Failed to activate a new conversation.')
      }
    },

    /** Sets a specific global conversation as the active chat. */
    async setCurrentConversation(conversationId: string) {
      log(`🌍 Setting current global conversation to: ${conversationId}`)
      const conversation = this.conversations.find((c) => c.id === conversationId)

      if (!conversation) {
        logError(
          `Conversation with ID ${conversationId} not found in the list. Cannot set as active.`
        )
        this.setError(`Conversation ${conversationId} not found.`)
        // Should we reset active chat here? Maybe not, let the caller handle it.
        return
      }

      try {
        // Set base active chat context (this also updates currentConversationDetails)
        this.setActiveChat(ChatType.GLOBAL, conversationId)

        // Load history for this conversation (important!)
        await this.getChatHistory(ChatType.GLOBAL, conversationId)

        // Move conversation to the top of the list for UI ordering preference (optional, getter sorts by date)
        const index = this.conversations.findIndex((c) => c.id === conversationId)
        if (index > 0) {
          const [activeConv] = this.conversations.splice(index, 1)
          if (activeConv) {
            this.conversations.unshift(activeConv)
          }
        }

        log(`✅ Successfully set active global conversation to ${conversationId}`)
      } catch (error) {
        // Errors likely from getChatHistory or setActiveChat's internal logic
        logError(`Error setting current conversation ${conversationId}:`, error)
        // setError is likely already called by getChatHistory on failure
        // Maybe reset active state if setting fails completely?
        if (this.activeIdentifier === conversationId) {
          this.cleanupChat(generateChatId(ChatType.GLOBAL, conversationId)) // Cleanup if activation failed
        }
        // Rethrow? Or just rely on error state being set?
        // throw error; // Propagate if needed
      }
    },

    /** Deletes a global conversation from the backend and removes it locally. */
    async deleteConversation(conversationId: string) {
      log(`🗑️ Deleting global conversation: ${conversationId}`)
      const chatId = generateChatId(ChatType.GLOBAL, conversationId)
      this.setLoading(true)
      this.setError(null)

      try {
        const config = useRuntimeConfig()
        const response = await fetch(
          `${config.public.apiBase}/chat/global/${conversationId}/messages`, // Assuming this deletes the conversation record too
          {
            method: 'DELETE',
            credentials: 'include',
          }
        )

        log(`Delete conversation response status for ${conversationId}: ${response.status}`)
        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(
            `Failed to delete conversation ${conversationId} (${response.status}): ${errorText}`
          )
        }

        // Remove from conversation list
        this.conversations = this.conversations.filter((c) => c.id !== conversationId)

        // Cleanup local data (messages, status etc.) & reset active state if it was deleted
        this.cleanupChat(chatId)

        log(`✅ Successfully deleted conversation ${conversationId}`)

        // Optional: Activate another conversation? Maybe the most recent one?
        // Or let the UI decide what to do (e.g., show a placeholder or the next convo)
        // if (this.conversations.length > 0) {
        //    await this.setCurrentConversation(this.sortedConversations[0].id);
        // }
      } catch (error) {
        logError(`Failed to delete conversation ${conversationId}:`, error)
        this.setError(
          `Failed to delete conversation: ${error instanceof Error ? error.message : String(error)}`
        )
      } finally {
        this.setLoading(false)
      }
    },

    async removeBubbleFromContext(conversationId: string, bubbleUuid: string) {
      log('✨ chatStore: ACTION CALLED - removeBubbleFromContext', { conversationId, bubbleUuid })
      if (!conversationId || !bubbleUuid) {
        const err = new Error(
          'Conversation ID and Bubble ID are required for removeBubbleFromContext.'
        )
        logError(err.message, { conversationId, bubbleUuid })
        throw err
      }

      this.setLoading(true) // Optional: indicate loading state
      this.setError(null)

      try {
        const config = useRuntimeConfig()
        if (!config.public || !config.public.apiBase) {
          throw new Error('API base URL is not configured in runtime config (public.apiBase).')
        }
        const apiBase = config.public.apiBase
        // API endpoint: DELETE /chat/{identifier}/bubble_source/{bubble_source_id}
        const apiUrl = `${apiBase}/chat/${conversationId}/bubble_source/${bubbleUuid}`
        log('✨ chatStore: Calling API DELETE', { apiUrl })

        await $fetch(apiUrl, {
          method: 'DELETE',
          credentials: 'include',
        })

        log('✨ chatStore: API call successful for removing bubble. Updating local store.', {
          conversationId,
          bubbleUuid,
        })

        const conversation = this.conversations.find((conv) => conv.id === conversationId)
        if (conversation) {
          if (conversation.relevantBubbles && Array.isArray(conversation.relevantBubbles)) {
            const index = conversation.relevantBubbles.indexOf(bubbleUuid)
            if (index > -1) {
              conversation.relevantBubbles.splice(index, 1)
            }
          }
          // conversation.updatedAt = new Date(); // Optionally update timestamp
        }

        if (
          this.activeIdentifier === conversationId &&
          this.currentConversationDetails?.relevantBubbles
        ) {
          if (Array.isArray(this.currentConversationDetails.relevantBubbles)) {
            const activeIndex = this.currentConversationDetails.relevantBubbles.indexOf(bubbleUuid)
            if (activeIndex > -1) {
              this.currentConversationDetails.relevantBubbles.splice(activeIndex, 1)
            }
          }
        }
        log('✨ chatStore: Local store updated successfully after removing bubble.')
      } catch (error) {
        logError('chatStore: API error in removeBubbleFromContext', {
          error,
          conversationId,
          bubbleUuid,
        })
        this.setError(
          `Failed to remove bubble: ${error instanceof Error ? error.message : String(error)}`
        )
        throw error
      } finally {
        this.setLoading(false)
      }
    },

    async addBubblesToConversationContext(conversationId: string, bubbleUuids: string[]) {
      log('✨ chatStore: ACTION CALLED - addBubblesToConversationContext', {
        conversationId,
        bubbleUuids,
      })
      if (!conversationId || !bubbleUuids || bubbleUuids.length === 0) {
        const err = new Error('Conversation ID and at least one Bubble ID are required.')
        logError(err.message, { conversationId, bubbleUuids })
        throw err
      }

      this.setLoading(true)
      this.setError(null)

      try {
        const config = useRuntimeConfig()
        if (!config.public || !config.public.apiBase) {
          throw new Error('API base URL is not configured in runtime config (public.apiBase).')
        }
        const apiBase = config.public.apiBase
        // API endpoint: POST /chat/{identifier}/bubble_source
        // Body: { "bubbleSourceIds": ["list_of_uuids"] }
        const apiUrl = `${apiBase}/chat/${conversationId}/bubble_source`
        log('✨ chatStore: Calling API POST to add bubbles', {
          apiUrl,
          payload: { bubbleSourceIds: bubbleUuids },
        })

        await $fetch(apiUrl, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: { bubbleSourceIds: bubbleUuids },
        })

        log('✨ chatStore: API call successful for adding bubbles. Updating local store.', {
          conversationId,
          bubbleUuids,
        })

        const conversation = this.conversations.find((conv) => conv.id === conversationId)
        if (conversation) {
          if (!conversation.relevantBubbles) {
            conversation.relevantBubbles = []
          }
          bubbleUuids.forEach((uuid) => {
            if (!conversation.relevantBubbles.includes(uuid)) {
              conversation.relevantBubbles.push(uuid)
            }
          })
          // conversation.updatedAt = new Date(); // Optionally update timestamp
        }

        if (this.activeIdentifier === conversationId && this.currentConversationDetails) {
          const currentDetails = this.currentConversationDetails // Alias for type narrowing

          bubbleUuids.forEach((uuid) => {
            if (!currentDetails.relevantBubbles.includes(uuid)) {
              currentDetails.relevantBubbles.push(uuid)
            }
          })
        }
        log('✨ chatStore: Local store updated successfully after adding bubbles.')
      } catch (error) {
        logError('chatStore: API error in addBubblesToConversationContext', {
          error,
          conversationId,
          bubbleUuids,
        })
        this.setError(
          `Failed to add bubbles to context: ${error instanceof Error ? error.message : String(error)}`
        )
        throw error
      } finally {
        this.setLoading(false)
      }
    },
  },
})
